export interface PageVerticalSignals {
  hostname?: string;
  explicitVertical?: string;
  pathname?: string;
  title?: string;
  headings?: string[];
  bodyClassNames?: string[];
  metaKeywords?: string;
  metaDescription?: string;
}

export type PageVerticalSource = "explicit" | "inferred_offer" | "site_default";

export interface ResolvedPageVertical {
  vertical: string;
  source: PageVerticalSource;
}

const VERTICAL_ALIASES: Record<string, string> = {
  altro: "altro",
  "altri-temi": "altro",
  automotive: "automotive",
  auto: "automotive",
  business: "business",
  b2b: "business",
  cultura: "culturepop",
  culturapop: "culturepop",
  culturepop: "culturepop",
  deal: "offers",
  deals: "offers",
  elettronica: "altro",
  ev: "automotive",
  gaming: "gaming",
  giochi: "gaming",
  hardware: "hardware",
  mobile: "mobile",
  offerte: "offers",
  "offerte-coupon": "offers",
  "offerte-e-sconti": "offers",
  offerta: "offers",
  offers: "offers",
  science: "science",
  scienze: "science",
  sconti: "offers",
  "sconti-coupon": "offers",
  sconto: "offers",
  smartphone: "mobile",
  videogiochi: "gaming",
  videogioco: "gaming"
};

const OFFER_PATH_PATTERNS = [
  /(^|\/)(offerte?|sconti?|deals?|coupon)(\/|$)/u,
  /(^|\/)(black-friday|cyber-monday|prime-day)(\/|$)/u
];

const OFFER_TEXT_MARKERS = [
  "offerta",
  "offerte",
  "sconto",
  "sconti",
  "deal",
  "deals",
  "coupon",
  "black friday",
  "cyber monday",
  "prime day",
  "promozione",
  "promozioni"
];

export function normalizeVertical(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = normalizeFreeText(value).replace(/\s+/gu, "-").replace(/-+/gu, "-").replace(/^-|-$/gu, "");
  if (!normalized) {
    return undefined;
  }

  return VERTICAL_ALIASES[normalized] ?? normalized;
}

export function normalizeScopeVertical(value: string | null | undefined): string {
  return normalizeVertical(value) ?? "default";
}

export function looksLikeOffersPage(signals: PageVerticalSignals): boolean {
  const pathname = normalizePath(signals.pathname);
  if (pathname && OFFER_PATH_PATTERNS.some((pattern) => pattern.test(pathname))) {
    return true;
  }

  const textSignals = [
    signals.title,
    signals.metaDescription,
    signals.metaKeywords,
    ...(signals.headings ?? []),
    ...(signals.bodyClassNames ?? [])
  ]
    .map((value) => normalizeFreeText(value))
    .filter((value) => value.length > 0);

  if (textSignals.length === 0) {
    return false;
  }

  const combined = textSignals.join(" ");
  const matchedMarkers = new Set<string>();
  for (const marker of OFFER_TEXT_MARKERS) {
    if (combined.includes(marker)) {
      matchedMarkers.add(marker);
    }
  }

  return matchedMarkers.size >= 2;
}

export function resolvePageVertical(signals: PageVerticalSignals): ResolvedPageVertical {
  const explicitVertical = normalizeVertical(signals.explicitVertical);
  if (explicitVertical) {
    return {
      vertical: explicitVertical,
      source: "explicit"
    };
  }

  if (looksLikeOffersPage(signals)) {
    return {
      vertical: "offers",
      source: "inferred_offer"
    };
  }

  return {
    vertical: resolveSiteDefaultVertical(signals.hostname),
    source: "site_default"
  };
}

function resolveSiteDefaultVertical(hostname: string | undefined): string {
  const normalizedHostname = normalizeHostname(hostname);
  if (normalizedHostname.includes("spaziogames")) {
    return "gaming";
  }

  return "unknown";
}

function normalizeHostname(value: string | undefined): string {
  if (!value) {
    return "";
  }

  return value.trim().toLocaleLowerCase().replace(/^www\./u, "");
}

function normalizePath(value: string | undefined): string {
  if (!value) {
    return "";
  }

  return value.trim().toLocaleLowerCase();
}

function normalizeFreeText(value: string | null | undefined): string {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLocaleLowerCase()
    .replace(/[_-]+/gu, " ")
    .replace(/[^\p{L}\p{N}\s/]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}
